import { Diorama } from '../game/three/Diorama';
import { Sim } from '../game/simulation';
import { ICON } from './icons';

/** Any HUD component that can resolve a chip's screen position by `data-id`.
 *  Decoupled from the specific class so FlyToHud doesn't care whether the
 *  legacy TopBar or the new InfoCard is the source. */
interface HudTargetResolver { target(id: string): { x: number; y: number }; }

interface Flyer {
  el: HTMLDivElement;
  x: number; y: number;
  tx: number; ty: number;
  t: number;
  dur: number;
  /** Target chip's `data-id`. Used at landing time to bounce the chip
   *  and emit `itemLanded` for the audio cue. */
  targetId: string;
  /** Window setTimeout id — fires only if normal cleanup didn't run
   *  (tab hidden, rAF paused). Cleared on natural removal. */
  watchdog: number;
}

/**
 * Animates resource glyphs from a world tile to the matching HUD chip.
 * On arrival the chip bounces (CSS class) and an `itemLanded` event
 * fires so the Sounds layer can play a per-item pop.
 *
 * Subscribes to sim events; reads tile screen-positions from the Diorama.
 */
export class FlyToHud {
  private layer: HTMLDivElement;
  private flyers: Flyer[] = [];

  constructor(root: HTMLElement, private sim: Sim, private diorama: Diorama, private hud: HudTargetResolver) {
    this.layer = document.createElement('div');
    this.layer.id = 'float-layer';
    root.appendChild(this.layer);

    sim.bus.on(e => {
      if (e.type === 'harvested') {
        const start = this.diorama.tileScreen(e.col, e.row);
        if (!start) return;
        // Pick chip + icon by the actual cropType so corn/carrot harvests
        // don't all fly into the wheat chip.
        const map: Record<string, { icon: string; id: 'basket' | 'corn' | 'carrot' }> = {
          wheat:  { icon: ICON.wheat,  id: 'basket' },
          corn:   { icon: ICON.corn,   id: 'corn' },
          carrot: { icon: ICON.carrot, id: 'carrot' },
        };
        const m = map[e.cropType] ?? map.wheat;
        const tgt = this.hud.target(m.id);
        this.spawn(m.icon, start.x, start.y, tgt.x, tgt.y, 0.6, m.id);
        this.popText(start.x, start.y, '+1', '#f4d03f');
      } else if (e.type === 'subCellHarvested') {
        const start = this.diorama.tileScreen(e.col, e.row);
        if (!start) return;
        const map: Record<string, { icon: string; id: 'basket' | 'corn' | 'carrot' }> = {
          wheat:  { icon: ICON.wheat,  id: 'basket' },
          corn:   { icon: ICON.corn,   id: 'corn' },
          carrot: { icon: ICON.carrot, id: 'carrot' },
        };
        const m = map[e.cropType];
        if (m) {
          const tgt = this.hud.target(m.id);
          this.spawn(m.icon, start.x, start.y, tgt.x, tgt.y, 0.6, m.id);
        }
      } else if (e.type === 'foraged') {
        const start = this.diorama.tileScreen(e.col, e.row);
        if (!start) return;
        const icon = e.kind === 'mushroom' ? ICON.mushroom : ICON.berry;
        const tgt = this.hud.target(e.kind);
        for (let i = 0; i < Math.min(e.amount, 4); i++) {
          this.spawn(icon, start.x, start.y, tgt.x, tgt.y, 0.6 + i * 0.06, e.kind);
        }
        this.popText(start.x, start.y - 14, `+${e.amount} ${e.kind}${e.amount === 1 ? '' : 's'}`, e.kind === 'mushroom' ? '#c83838' : '#4060a0');
      } else if (e.type === 'sold' || e.type === 'truckReturned') {
        if (e.coins <= 0) return;
        const tgt = this.hud.target('money');
        const cx = window.innerWidth / 2, cy = window.innerHeight / 2;
        const n = Math.min(8, Math.max(3, Math.floor(e.coins / 5)));
        for (let i = 0; i < n; i++) {
          const ang = (i / n) * Math.PI * 2;
          const sx = cx + Math.cos(ang) * 24;
          const sy = cy + Math.sin(ang) * 24;
          this.spawn(ICON.coin, sx, sy, tgt.x, tgt.y, 0.7 + i * 0.05, 'money');
        }
        this.popText(cx, cy - 30, `+${e.coins}`, '#f4d03f');
      } else if (e.type === 'millFinished') {
        const id = e.kind === 'corn' ? 'cornFlour' : 'flour';
        const tgt = this.hud.target(id);
        const start = this.diorama.tileScreen(e.col, e.row);
        if (!start) return;
        for (let i = 0; i < 5; i++) this.spawn(ICON.flour, start.x, start.y, tgt.x, tgt.y, 0.6 + i * 0.08, id);
        const label = e.kind === 'corn' ? 'corn flour' : 'flour';
        this.popText(start.x, start.y - 20, `+${e.flour} ${label}`, '#f0e6c8');
      } else if (e.type === 'planted') {
        const start = this.diorama.tileScreen(e.col, e.row);
        if (start) this.popIcon(start.x, start.y - 12, ICON.sprout);
      } else if (e.type === 'fieldPlaced') {
        const start = this.diorama.tileScreen(e.col, e.row);
        if (start) this.popText(start.x, start.y - 12, '+field', '#9b7320');
      } else if (e.type === 'chopped') {
        const start = this.diorama.tileScreen(e.col, e.row);
        if (!start) return;
        const wood      = e.wood      ?? 0;
        const stone     = e.stone     ?? 0;
        const acorns    = e.acorns    ?? 0;
        const mushrooms = e.mushrooms ?? 0;
        const berries   = e.berries   ?? 0;
        if (wood > 0) {
          const tgt = this.hud.target('wood');
          for (let i = 0; i < Math.min(wood, 3); i++) {
            this.spawn(ICON.wood, start.x, start.y, tgt.x, tgt.y, 0.6 + i * 0.06, 'wood');
          }
          this.popText(start.x, start.y - 12, `+${wood} wood`, '#a06030');
        }
        if (stone > 0) {
          const tgt = this.hud.target('stone');
          for (let i = 0; i < Math.min(stone, 3); i++) {
            this.spawn(ICON.stone, start.x, start.y, tgt.x, tgt.y, 0.6 + i * 0.06, 'stone');
          }
          this.popText(start.x, start.y - 12, `+${stone} stone`, '#909090');
        }
        if (acorns > 0) {
          const tgt = this.hud.target('treeSeeds');
          for (let i = 0; i < Math.min(acorns, 3); i++) {
            this.spawn(ICON.acorn, start.x, start.y, tgt.x, tgt.y, 0.7 + i * 0.06, 'treeSeeds');
          }
          this.popText(start.x + 30, start.y - 12, `+${acorns} acorn${acorns === 1 ? '' : 's'}`, '#8a5a30');
        }
        if (mushrooms > 0) {
          // Salvaged from clearing a fungus-bearing stump. Slightly slower
          // duration so the player sees them peel off after the wood.
          const tgt = this.hud.target('mushroom');
          for (let i = 0; i < Math.min(mushrooms, 3); i++) {
            this.spawn(ICON.mushroom, start.x, start.y, tgt.x, tgt.y, 0.8 + i * 0.07, 'mushroom');
          }
          this.popText(start.x - 30, start.y - 28, `+${mushrooms} mushroom${mushrooms === 1 ? '' : 's'}`, '#c83838');
        }
        if (berries > 0) {
          // Salvaged from clearing a fruiting bush.
          const tgt = this.hud.target('berry');
          for (let i = 0; i < Math.min(berries, 3); i++) {
            this.spawn(ICON.berry, start.x, start.y, tgt.x, tgt.y, 0.8 + i * 0.07, 'berry');
          }
          this.popText(start.x - 30, start.y - 28, `+${berries} berr${berries === 1 ? 'y' : 'ies'}`, '#4060a0');
        }
      } else if (e.type === 'seedsBought') {
        // Order placed (not yet delivered). Fly a couple of coin icons
        // FROM the money chip TO the truck so the player sees their
        // payment going onto the truck's "shopping list". Pop a
        // confirmation label at the truck.
        const tk = this.diorama.truckScreen();
        const moneyTgt = this.hud.target('money');
        if (tk) {
          this.spawn(ICON.coin, moneyTgt.x, moneyTgt.y, tk.x, tk.y, 0.55, '');
          this.spawn(ICON.coin, moneyTgt.x, moneyTgt.y, tk.x, tk.y, 0.65, '');
          this.popText(tk.x, tk.y - 12, `Ordered +${e.qty} ${e.seed}`, '#d28030');
        } else {
          const cx = window.innerWidth / 2, cy = window.innerHeight - 80;
          this.popText(cx, cy - 30, `Ordered +${e.qty} ${e.seed}`, '#d28030');
        }
      } else if (e.type === 'seedsDelivered') {
        // Truck just returned with the seed errand — fly the seeds FROM
        // the truck's screen position TO the StorageBar's per-seed chip
        // (seedWheat / seedCorn / seedCarrot) in the bottom-right corner.
        // StorageBar exposes these via data-id attributes that target()
        // resolves through the document.
        const tk = this.diorama.truckScreen();
        if (!tk) return;
        const icon = e.seed === 'wheat' ? ICON.wheat : e.seed === 'corn' ? ICON.corn : ICON.carrot;
        const chipId =
          e.seed === 'wheat'  ? 'seedWheat' :
          e.seed === 'corn'   ? 'seedCorn'  :
                                 'seedCarrot';
        const tgt = this.hud.target(chipId);
        const n = Math.min(e.qty, 8);
        for (let i = 0; i < n; i++) {
          this.spawn(icon, tk.x, tk.y, tgt.x, tgt.y, 0.7 + i * 0.05, chipId);
        }
        this.popText(tk.x, tk.y - 12, `+${e.qty} ${e.seed}`, '#5a9c40');
      } else if (e.type === 'truckLoaded') {
        // Player loaded items toward the truck. Fly the icon FROM its
        // HUD chip DOWN to wherever the cubes will actually land:
        //   - Truck parked at depot → onto the truck bed.
        //   - Truck on the road (departed/returning) → onto the depot
        //     platform pallet, which is where the cubes appear.
        // Without this split the icons follow the truck mid-trip and
        // miss the platform entirely.
        const tStatus = this.sim.state.truck.status;
        const isAway = tStatus === 'departed' || tStatus === 'returning';
        const tk = isAway ? this.diorama.platformScreen() : this.diorama.truckScreen();
        if (!tk) return;
        const cargoIconMap: Record<string, { icon: string; chip: string }> = {
          wheat:     { icon: ICON.wheat,    chip: 'basket' },
          corn:      { icon: ICON.corn,     chip: 'corn' },
          carrot:    { icon: ICON.carrot,   chip: 'carrot' },
          flour:     { icon: ICON.flour,    chip: 'flour' },
          cornFlour: { icon: ICON.flour,    chip: 'cornFlour' },
          wood:      { icon: ICON.wood,     chip: 'wood' },
          mushroom:  { icon: ICON.mushroom, chip: 'mushroom' },
          berry:     { icon: ICON.berry,    chip: 'berry' },
          acorn:     { icon: ICON.acorn,    chip: 'treeSeeds' },
        };
        const m = cargoIconMap[e.kind];
        if (!m) return;
        const start = this.hud.target(m.chip as any);
        if (!start) return;
        const n = Math.min(e.qty, 8);
        for (let i = 0; i < n; i++) {
          this.spawn(m.icon, start.x, start.y, tk.x, tk.y, 0.55 + i * 0.05, '');
        }
      } else if (e.type === 'materialsDeposited') {
        // Construction crate accepts the materials — fly each kind from
        // its HUD chip to the building tile so the player visibly sees
        // wood / wheat / stone / coins moving onto the site.
        const tgt = this.diorama.tileScreen(e.col, e.row);
        if (!tgt) return;
        const matMap: Record<string, { icon: string; chip: string }> = {
          money: { icon: ICON.coin,  chip: 'money'  },
          wood:  { icon: ICON.wood,  chip: 'wood'   },
          wheat: { icon: ICON.wheat, chip: 'basket' },
          stone: { icon: ICON.stone,  chip: 'stone'  },
        };
        const m = matMap[e.kind];
        if (!m) return;
        const start = this.hud.target(m.chip as any);
        if (!start) return;
        const n = Math.min(e.qty, 6);
        for (let i = 0; i < n; i++) {
          this.spawn(m.icon, start.x, start.y, tgt.x, tgt.y, 0.55 + i * 0.05, '');
        }
      }
    });
  }

  /** Hard cap so a rapid burst (e.g. truck unload with 50+ items)
   *  doesn't strand hundreds of DOM nodes if the tab is hidden during
   *  the flight. The oldest active flyer is force-removed when the
   *  cap is hit. */
  private static MAX_FLYERS = 80;
  private spawn(glyph: string, x: number, y: number, tx: number, ty: number, dur = 0.6, targetId = '') {
    while (this.flyers.length >= FlyToHud.MAX_FLYERS) {
      const oldest = this.flyers.shift();
      if (oldest) oldest.el.remove();
    }
    const el = document.createElement('div');
    el.className = 'float-item glyph';
    el.innerHTML = glyph;
    el.style.left = x + 'px';
    el.style.top = y + 'px';
    this.layer.appendChild(el);
    // Watchdog removal — if update() stops being called (tab hidden,
    // rAF paused), the flyer would otherwise sit in the DOM forever.
    // Fires well after the natural lifetime so it doesn't fight normal
    // cleanup.
    const watchdog = window.setTimeout(() => {
      if (el.isConnected) el.remove();
    }, Math.ceil(dur * 1000) + 2500);
    this.flyers.push({ el, x, y, tx, ty, t: dur, dur, targetId, watchdog });
  }

  private popText(x: number, y: number, text: string, color: string) {
    const el = document.createElement('div');
    el.className = 'float-text';
    el.textContent = text;
    el.style.color = color;
    el.style.left = x + 'px';
    el.style.top = y + 'px';
    this.layer.appendChild(el);
    setTimeout(() => {
      el.style.transition = 'transform .8s ease-out, opacity .8s';
      el.style.transform = 'translate(-50%, -30px)';
      el.style.opacity = '0';
    }, 10);
    setTimeout(() => el.remove(), 900);
  }

  private popIcon(x: number, y: number, icon: string) {
    const el = document.createElement('div');
    el.className = 'float-text float-icon glyph';
    el.innerHTML = icon;
    el.style.left = x + 'px';
    el.style.top = y + 'px';
    this.layer.appendChild(el);
    setTimeout(() => {
      el.style.transition = 'transform .8s ease-out, opacity .8s';
      el.style.transform = 'translate(-50%, -30px)';
      el.style.opacity = '0';
    }, 10);
    setTimeout(() => el.remove(), 900);
  }

  /** Trigger the chip-land bounce + sound for `id`. Safe to call rapidly;
   *  the CSS animation restart trick (toggle class then read offsetWidth)
   *  ensures successive lands re-play the bounce rather than ignoring it. */
  private landOnChip(id: string) {
    if (!id) return;
    const chip = document.querySelector(`[data-id="${id}"]`) as HTMLElement | null;
    if (chip) {
      chip.classList.remove('is-landing');
      // Force reflow so the next class add restarts the animation.
      void chip.offsetWidth;
      chip.classList.add('is-landing');
      // Strip the class after the animation finishes so back-to-back lands
      // restart cleanly. The CSS animation runs ~250ms.
      setTimeout(() => chip.classList.remove('is-landing'), 320);
    }
    // Bus event so Sounds (and any future listener) can react.
    this.sim.bus.emit({ type: 'itemLanded', id });
  }

  update(dt: number) {
    const alive: Flyer[] = [];
    for (const f of this.flyers) {
      f.t -= dt;
      if (f.t <= 0) {
        // Snap the flyer to its destination chip BEFORE removing it so
        // the eye registers the icon "merging" with the chip.
        f.el.style.left = f.tx + 'px';
        f.el.style.top = f.ty + 'px';
        f.el.style.opacity = '0';
        // Cleanup the DOM a beat later so the snap is visible.
        const el = f.el;
        setTimeout(() => el.remove(), 80);
        // Natural cleanup ran — no need for the watchdog timer.
        if (f.watchdog) clearTimeout(f.watchdog);
        this.landOnChip(f.targetId);
        continue;
      }
      const progress = 1 - f.t / f.dur;
      const px = f.x + (f.tx - f.x) * Math.min(1, progress * progress);
      const py = f.y + (f.ty - f.y) * Math.min(1, progress * progress) - Math.sin(progress * Math.PI) * 30;
      f.el.style.left = px + 'px';
      f.el.style.top = py + 'px';
      f.el.style.opacity = Math.min(1, f.t * 3) + '';
      alive.push(f);
    }
    this.flyers = alive;
  }
}
